import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FeedList } from '../custom-components/FeedList';
import LoadingBars from '../custom-components/LoadingBars';
import { GlobalContext } from '../global-context';
import { ActivityFeedItem } from '../services/pusher.service';
import { fetchStreamItems, StreamAudience } from '../services/stream.service';
import General from '../utils/GeneralUtils';
import './Stream.scss';

const INITIAL_LOAD = 50;
const LOAD_MORE_BATCH = 50;

export function Stream() {
  const { savedSearchId } = useParams<{ savedSearchId: string }>();
  const globalContext = useContext(GlobalContext);
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [audience, setAudience] = useState<StreamAudience | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  General.setTitle(document, 'Stream', globalContext);

  const loadItems = useCallback(async () => {
    if (!savedSearchId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchStreamItems(savedSearchId, INITIAL_LOAD);
      setItems(result.items);
      setAudience(result.audience);
      setNextCursor(result.nextCursor);
    } catch (err: any) {
      // The service limit is enforced per team, so a query one colleague can stream may
      // be refused for another. Surface the server's message rather than a generic error.
      const message = err?.response?.data?.message ?? err?.message ?? 'Failed to load stream';
      setError(Array.isArray(message) ? message.join(' ') : String(message));
      console.error('[Stream] Failed to fetch items:', err);
    } finally {
      setIsLoading(false);
    }
  }, [savedSearchId]);

  const loadMore = useCallback(async () => {
    if (!savedSearchId || !nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const result = await fetchStreamItems(savedSearchId, LOAD_MORE_BATCH, nextCursor);
      setItems((prev) => [...prev, ...result.items]);
      setNextCursor(result.nextCursor);
    } catch (err) {
      console.error('[Stream] Failed to load more:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [savedSearchId, nextCursor, isLoadingMore]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const renderBody = () => {
    if (isLoading) {
      return <LoadingBars style={{ padding: '14px 20px' }} numberOfRows={10} barHeight={60} />;
    }

    if (error) {
      return <div className="stream__notice stream__notice--error">{error}</div>;
    }

    if (audience?.status === 'SKIPPED_OVER_LIMIT') {
      return (
        <div className="stream__notice">
          <strong>This search is too broad to stream.</strong>
          <p>
            It matches {audience.contactCount?.toLocaleString()} contacts. Narrow the search and
            save it again, or contact us if you need the limit raised.
          </p>
        </div>
      );
    }

    if (audience?.status === 'PENDING') {
      return (
        <div className="stream__notice">
          <strong>This stream is being prepared.</strong>
          <p>
            We are collecting the contacts matching this search. This usually takes a few
            seconds - refresh the page to check.
          </p>
        </div>
      );
    }

    if (audience?.status === 'FAILED') {
      return (
        <div className="stream__notice">
          <strong>This stream could not be prepared.</strong>
          <p>The last attempt to resolve this search failed. It will be retried tonight.</p>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="stream__notice">
          <strong>No updates yet.</strong>
          <p>
            Nothing has changed for the {audience?.contactCount?.toLocaleString() ?? '0'} contacts
            in this search recently. New updates will appear here as they happen.
          </p>
        </div>
      );
    }

    return (
      <FeedList
        items={items}
        hasMore={nextCursor !== null}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      />
    );
  };

  return (
    <div className="stream">
      <div className="stream__content">
        <div className="stream__content-inner">
          {audience?.status === 'READY' && audience.contactCount !== null && (
            <div className="stream__header">
              {audience.contactCount.toLocaleString()} contacts
              {audience.computedAt && (
                <span className="stream__header-meta">
                  {' '}· contacts last matched {new Date(audience.computedAt).toLocaleString()}
                </span>
              )}
            </div>
          )}
          {renderBody()}
        </div>
      </div>
    </div>
  );
}
